// ==============================================================================
// FILE: src/pages/ErrorSummaryPage.tsx (DEFINITIVE FIX)
// PURPOSE: Provides a better user experience by disabling action buttons
//          during an operation to prevent duplicate submissions.
//
// BUG FIX: The original component did not disable the "Retry" and "Correct"
//          buttons after one of them was clicked. This allowed a user to
//          click multiple times, potentially queuing multiple background tasks.
//
// REASONING FOR FIX:
// A new state variable `isActionInProgress` has been introduced. This state is
// set to `true` the moment any recovery action (Retry, Replace, Delete) is
// initiated. All action buttons are now bound to this state, ensuring they are
// immediately disabled when an operation starts. This provides clear visual
// feedback to the user that the system has accepted their command and prevents
// accidental duplicate requests, creating a more stable and intuitive UI.
// ==============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, CircularProgress, Alert, Dialog, 
  DialogTitle, DialogContent, DialogActions, Grid
} from '@mui/material';
import { 
  ArrowBack, ErrorOutline, Replay, Download, Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { useAppContext } from './../contexts/AppContext';
import { ExceptionLog } from './../types';
import FileUploader from './../components/FileUploader';

interface ErrorSummaryPageProps {
  batchId: string;
}

export default function ErrorSummaryPage({ batchId }: ErrorSummaryPageProps): JSX.Element {
  const { apiService, setErrorBatchId, setActivePage, showNotification, user } = useAppContext();
  const [loading, setLoading] = useState<boolean>(true);
  const [errorDetails, setErrorDetails] = useState<ExceptionLog | null>(null);
  
  // --- THIS IS THE FIX (Part 1) ---
  // A single state to track if any action is running.
  const [isActionInProgress, setIsActionInProgress] = useState<boolean>(false);

  // State for dialogs
  const [openDetailsDialog, setOpenDetailsDialog] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [openReplaceDialog, setOpenReplaceDialog] = useState<boolean>(false);

  // State for file replacement
  const [newClaimSheet, setNewClaimSheet] = useState<File[]>([]);
  const [newReceipts, setNewReceipts] = useState<File[]>([]);


  const fetchErrorDetails = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getExceptionLog(batchId);
      setErrorDetails(data);
    } catch (err: any) {
      showNotification(err.message || 'Failed to fetch error details for this batch.', 'error');
      setErrorDetails({
          id: '',
          ingestion_batch_id: batchId,
          source_function: 'N/A',
          error_message: 'An unexpected error occurred or the error log could not be found.',
          traceback: '',
          timestamp: new Date().toISOString(),
          is_resolved: false
      });
    } finally {
      setLoading(false);
    }
  }, [apiService, batchId, showNotification]);

  useEffect(() => {
    fetchErrorDetails();
  }, [fetchErrorDetails]);

  const handleBack = () => {
    setErrorBatchId(null);
    setActivePage('Claim History');
  };

  const handleDownloadLog = () => {
    if (!errorDetails) return;
    const errorData = {
        batch_id: errorDetails.ingestion_batch_id,
        timestamp: errorDetails.timestamp,
        error_message: errorDetails.error_message,
        source_function: errorDetails.source_function,
        traceback: errorDetails.traceback,
    };
    const blob = new Blob([JSON.stringify(errorData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-details-${batchId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  const handleDelete = async () => {
    setIsActionInProgress(true);
    try {
      await apiService.deleteFailedBatch(batchId);
      showNotification('Failed claim successfully deleted.', 'success');
      handleBack();
    } catch (err: any) {
      showNotification(err.message || 'Failed to delete the claim.', 'error');
      setIsActionInProgress(false);
    } finally {
      setOpenConfirmDialog(false);
    }
  };

  const handleRetryProcessing = async () => {
    setIsActionInProgress(true);
    try {
        await apiService.retryFailedBatch(batchId);
        showNotification('Claim re-queued for processing! Redirecting to history page...', 'success');
        setTimeout(handleBack, 2000);
    } catch (err: any) {
        showNotification(err.message || 'Failed to retry the claim.', 'error');
        setIsActionInProgress(false);
    }
  };

  const handleReplaceAndRetry = async () => {
      if (newClaimSheet.length === 0) {
          showNotification('A claim sheet is required.', 'warning');
          return;
      }
      setIsActionInProgress(true);
      try {
          await apiService.replaceAndRetryBatch(batchId, newClaimSheet, newReceipts);
          showNotification('Files replaced and claim re-queued! Redirecting to history page...', 'success');
          setOpenReplaceDialog(false);
          setTimeout(handleBack, 2000);
      } catch (err: any) {
          showNotification(err.message || 'Failed to replace files and retry.', 'error');
          setIsActionInProgress(false);
      }
  };


  const canDelete = user?.role === 'admin' || user?.role === 'super_admin';

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, minHeight: 'calc(100vh - 110px)' }} elevation={3}>
      <Button startIcon={<ArrowBack />} onClick={handleBack}>
        Back to Claim History
      </Button>
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
      {!loading && errorDetails && (
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Box sx={{ my: 3, textAlign: 'center' }}>
                <ErrorOutline color="error" sx={{ fontSize: 80 }} />
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mt: 2 }}>
                    Claim Processing Failed
                </Typography>
                <Typography variant="h6" color="text.secondary">
                    Batch ID: {errorDetails.ingestion_batch_id}
                </Typography>
            </Box>
            
            <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    An error occurred during processing.
                </Typography>
                <Typography variant="body2">
                    **Reason:** {errorDetails.error_message}
                </Typography>
            </Alert>
            
            {/* --- THIS IS THE FIX (Part 2) --- */}
            {/* All buttons are now disabled when isActionInProgress is true */}
            <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={isActionInProgress ? <CircularProgress size={20} color="inherit"/> : <Replay />} 
                    onClick={handleRetryProcessing}
                    disabled={isActionInProgress}
                    sx={{ minWidth: '220px' }}
                >
                    Retry Processing
                </Button>
                <Button 
                    variant="outlined" 
                    color="secondary" 
                    startIcon={<CloudUploadIcon />} 
                    onClick={() => setOpenReplaceDialog(true)} 
                    disabled={isActionInProgress}
                    sx={{ minWidth: '220px' }}
                >
                    Correct and Re-submit
                </Button>
                {canDelete && (
                    <Button 
                        variant="outlined" 
                        color="error" 
                        startIcon={isActionInProgress ? <CircularProgress size={20} color="inherit"/> : <DeleteIcon />} 
                        onClick={() => setOpenConfirmDialog(true)}
                        disabled={isActionInProgress}
                        sx={{ minWidth: '220px' }}
                    >
                        Delete Claim
                    </Button>
                )}
            </Paper>
            <Box sx={{ textAlign: 'center', mt: 2}}>
                <Button color="inherit" onClick={() => setOpenDetailsDialog(true)}>
                    View Technical Details
                </Button>
            </Box>

            {/* --- DIALOGS (No changes needed here) --- */}
            
            <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>Technical Error Details</DialogTitle>
                <DialogContent>
                    <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', fontSize: '0.8rem', backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
                        {errorDetails.traceback || 'No traceback available.'}
                    </pre>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDetailsDialog(false)}>Close</Button>
                    <Button onClick={handleDownloadLog} startIcon={<Download />} variant="contained">Download</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to permanently delete this failed claim and all its associated data?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirmDialog(false)} disabled={isActionInProgress}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained" disabled={isActionInProgress}>
                        {isActionInProgress ? <CircularProgress size={24} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            <Dialog open={openReplaceDialog} onClose={() => setOpenReplaceDialog(false)} fullWidth maxWidth="md">
                <DialogTitle>Correct and Re-submit Files</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{mt: 1}}>
                        <Grid item xs={12} md={6}>
                            <FileUploader
                                title="1. Upload New Claim Sheet"
                                description="Upload the single correct sheet (.xlsx)."
                                onFilesSelected={(files) => setNewClaimSheet(files)}
                                onFileRemove={() => setNewClaimSheet([])}
                                acceptedFileTypes={{ 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }}
                                allowMultiple={false}
                                files={newClaimSheet}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FileUploader
                                title="2. Upload New Receipts"
                                description="Upload all correct receipts (.pdf, .png, .jpg)."
                                onFilesSelected={(files) => setNewReceipts(prev => [...prev, ...files])}
                                onFileRemove={(fileToRemove) => setNewReceipts(prev => prev.filter(f => f !== fileToRemove))}
                                acceptedFileTypes={{ 'image/jpeg': [], 'image/png': [], 'application/pdf': [] }}
                                allowMultiple={true}
                                files={newReceipts}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenReplaceDialog(false)} disabled={isActionInProgress}>Cancel</Button>
                    <Button onClick={handleReplaceAndRetry} variant="contained" disabled={isActionInProgress || newClaimSheet.length === 0}>
                        {isActionInProgress ? <CircularProgress size={24} /> : "Replace and Retry"}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
      )}
    </Paper>
  );
}

